'use client';

import { config } from '@/configs/env';
import { RNMessageType, sendMessageToRN } from '@/contexts/LayoutContext';
import { RefreshResult, sessionRefresher } from '@/lib/session';
import SendBrowserEvent from '@/services/SendBrowserEvent.service';
import { fetchDataFromCookies } from '@/services/util/cookies.util';
import { isReactNativeWebView } from '@/utils/common';
import { updateCallbackUrl } from '@/utils/path.util';

// Re-export so existing async importers (useBasket, cart.service, etc.) keep working.
// Initial-bundle consumers should import directly from '@/lib/errors' instead.
export { AuthError } from '@/lib/errors';

export class RedirectToLoginError extends Error {
  constructor(message = 'Redirecting to login') {
    super(message);
    this.name = 'RedirectToLoginError';
  }
}

type FetcherOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: HeadersInit;
  body?: unknown;
  requireAuth?: boolean;
  cache?: RequestCache;
  revalidate?: number;
  tags?: string[];
  isNative?: boolean;
  redirectLogin?: boolean;
};

let refreshInFlight: Promise<RefreshResult | null> | null = null;

async function refreshAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = sessionRefresher.refreshOnce().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

function redirectToLogin(isNative?: boolean) {
  if (typeof window === 'undefined') {
    throw new RedirectToLoginError();
  }

  const currentUrl = updateCallbackUrl();

  if (isNative || isReactNativeWebView()) {
    const url = `${config.ONBOARDING_URL}?callbackURI=${encodeURIComponent(
      currentUrl
    )}`;

    window.location.replace(url);
  } else {
    SendBrowserEvent.redirectToLoginPage();
  }

  throw new RedirectToLoginError();
}

export async function apiFetch<T>(
  url: string,
  options: FetcherOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    headers,
    body,
    requireAuth = false,
    cache = 'no-store',
    revalidate,
    tags,
    isNative,
    redirectLogin = true,
  } = options;

  const makeRequest = async (token?: string | null) => {
    return fetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      cache,
      next:
        cache !== 'no-store'
          ? {
              revalidate,
              tags,
            }
          : undefined,
    });
  };

  let token: string | null = null;

  if (requireAuth) {
    token = fetchDataFromCookies().accessToken ?? null;
  }
  // First request
  let res = await makeRequest(token);

  // If unauthorized -> try refresh once
  if (requireAuth && res.status === 401) {
    const refreshResult = await refreshAccessToken();
    // Refresh failed -> redirect to login
    if (!refreshResult || refreshResult.status !== 'ok') {
      if (!redirectLogin && isReactNativeWebView()) {
        sendMessageToRN({
          type: RNMessageType.AUTH_SESSION_EXPIRED,
          data: {},
        });
      }
      if (redirectLogin) {
        redirectToLogin(isNative);
      }
    }

    // Notify RN that token refreshed
    if (isReactNativeWebView()) {
      sendMessageToRN({
        type: RNMessageType.AUTH_TOKEN_REFRESHED,
        data: {
          sid: fetchDataFromCookies().sid,
        },
      });
    }

    // Retry request with fresh token
    const newToken = fetchDataFromCookies().accessToken ?? null;
    res = await makeRequest(newToken);

    // Still unauthorized after refresh
    if (redirectLogin && res.status === 401) {
      redirectToLogin(isNative);
    }
  }

  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';

    console.log(`Request failed (${res.status})`);
    let message = ``;

    if (contentType.includes('application/json')) {
      const data = await res.json().catch(() => null);

      if (data?.error.message) {
        message += `${data.error.message}`;
      }
    }

    throw new Error(message);
  }

  const contentType = res.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    return null as T;
  }

  return res.json();
}
const wait = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const waitForAuthCookies = async (maxTries = 1) => {
  for (let i = 0; i < maxTries; i++) {
    const { sid, accessToken } = fetchDataFromCookies();
    if (sid || accessToken) {
      return { sid, accessToken };
    }
    await wait(200);
  }

  return { sid: null, accessToken: null };
};

export const isUnauthorized = async () => {
  const { sid, accessToken } = await waitForAuthCookies();
  return !sid && !accessToken;
};
